/* Copyright (c) 2018 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import HEAttributeFooter from './components/HEAttributeFooter';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';

import DatePickerToggle from '../../components/DatePickerToggle';
import { TimeFromNow } from '../../localization/LocalizedDate';
import Spacer from '../../components/Spacer';
import ContentWrapper from '../../components/ContentWrapper';
import { Card, Inputs } from '../../../bphComponents/bundle';

import DateUtil from '../../../utilities/DateUtil';
import { emptyFuzzyDate } from '../../../utilities/FuzzyDate';
import { useNavigate } from '../../hooks/useNavigate';
import { useActiveProfile } from '../../hooks/useProfile';
import { useHealthEntryForActiveProfile } from '../../hooks/useHealthEntry';
import ProfileModel from '../../../models/ProfileModel';
import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';
import RouteNames from '../../appAuthorized/RouteNames';
import { useLists } from '../../hooks/useLists';

interface Props {
  match: any;
}

const HealthEntryDiagnosis: React.FC<Props> = (props) => {
  const healthEntryId = Number(props.match.params.healthEntryId);

  const [profileData] = useActiveProfile();
  const [{ category }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const [, navActions] = useNavigate();
  const [{ diagnosisList }] = useLists();
  const heInstance = heActions.instantiateHealthEntry();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [state, setState] = React.useState({
    date: { date: '', precision: '' },
    value: null,
  });

  const profile = new ProfileModel(profileData.data);
  const hasDiagnosis = heInstance.diagnosis && heInstance.diagnosis.hasValue();
  const confirmedSuspectedChoices = diagnosisList.map(d => ({
    name: 'diagnosis',
    value: d.id,
    label: <FormattedMessage id={`list.healthEntry.diagnosis.${d.name}.name`} />,
  }));

  React.useEffect(() => {
    if (category !== HealthEntryCategoryNames.missing) {
      setState({
        value: (heInstance.diagnosis && heInstance.diagnosis.getValue()) || diagnosisList[0].id,
        date: (heInstance.diagnosis && heInstance.diagnosis.getDate()) || emptyFuzzyDate,
      });
    }
  }, [category]);

  const toggleConfirmed = () => {
    const valueIdx = state.value === diagnosisList[0].id ? 1 : 0;

    setState({
      ...state,
      value: diagnosisList[valueIdx].id,
    });
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    const newHe = heActions.instantiateHealthEntry();
    newHe.diagnosis.set(state.date.date ? state.date : null, state.value);

    heActions.apiUpdate(newHe.data())
      .then(() => {
        setIsSubmitting(false);
        navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
      });
  };

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={isSubmitting}
          cancel={() => {
            navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
          }}
          submit={submitForm}
          color="white"
        />

        <ContentFrame>
          <ScrollAwareScroller>
            <AppHeaderScrollableText
              id="healthEntry.diagnosis.title.text"
            />
            <ContentWrapper>
              <div>
                <Card.CardShape>
                  <Card.CardContent>
                    <Inputs.ToggleSwitch.ProfileThemedToggleSwitch
                      uniqueID="diagnosis"
                      value={state.value}
                      choices={confirmedSuspectedChoices}
                      onChange={toggleConfirmed}
                    />

                    <Spacer size="9" />

                    <DatePickerToggle
                      label="healthEntry.diagnosis.date.field.label"
                      name="date"
                      onChange={(date) => setState({ ...state, date })}
                      isFuzzy
                      hasToday
                      allowNull
                      earliestDateLimit={DateUtil.subtract(profile.getDateOfBirth(), 1, 'year')}
                      earliestDateWarningLimit={profile.getDateOfBirth()}
                      warningHint="healthEntryDates.beforeBirth.warning.text"
                      regularHint={(
                        <TimeFromNow
                          dateData={state.date}
                        />
                      )}
                      {...state.date}
                    />
                  </Card.CardContent>
                </Card.CardShape>
              </div>
              {
                hasDiagnosis && (
                  <HEAttributeFooter
                    onDelete={() => {
                      const newHe = heActions.instantiateHealthEntry();
                      newHe.diagnosis.remove();
                      heActions.apiUpdate(newHe.data())
                        .then(() => {
                          navActions.goBack(RouteNames.healthEntry(healthEntryId));
                        });
                    }}
                    onToggleFlag={null}
                  />
                )
              }

            </ContentWrapper>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

export default HealthEntryDiagnosis;
